import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { timeTrackingApi } from "../services/timetracking-api";
import { EmployeeStatus } from "../types/timetracking";
import { useAuth } from "./AuthContext";
import { canAccessTimeTracking } from "../services/api";

interface WorkStatusContextType {
  workStatus: EmployeeStatus | null;
  refreshWorkStatus: () => Promise<void>;
  isLoading: boolean;
}

const WorkStatusContext = createContext<WorkStatusContextType | undefined>(
  undefined,
);

export const WorkStatusProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user } = useAuth();
  const [workStatus, setWorkStatus] = useState<EmployeeStatus | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const hasTimeTrackingAccess = canAccessTimeTracking(user || null);

  const refreshWorkStatus = useCallback(async () => {
    if (user?.id && hasTimeTrackingAccess) {
      setIsLoading(true);
      try {
        const status = await timeTrackingApi.getEmployeeStatus(user.id);
        setWorkStatus(status);
      } catch (error) {
        console.error("Failed to fetch work status:", error);
      } finally {
        setIsLoading(false);
      }
    }
  }, [user?.id, hasTimeTrackingAccess]);

  // Fetch work status on mount and periodically
  useEffect(() => {
    refreshWorkStatus();

    // Refresh status every 60 seconds
    const interval = setInterval(refreshWorkStatus, 60000);

    return () => clearInterval(interval);
  }, [refreshWorkStatus]);

  return (
    <WorkStatusContext.Provider
      value={{ workStatus, refreshWorkStatus, isLoading }}
    >
      {children}
    </WorkStatusContext.Provider>
  );
};

export const useWorkStatus = () => {
  const context = useContext(WorkStatusContext);
  if (!context) {
    throw new Error("useWorkStatus must be used within WorkStatusProvider");
  }
  return context;
};
